import { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import Color from 'color';
import { Robot, XLg } from 'react-bootstrap-icons';
import { createUseThemedStyles } from '@/hooks';
import { AiChat, AiChatRef } from '@/components/ai-chat';
import { AiConversation } from '@/lib/models';

const useStyles = createUseThemedStyles((theme) => ({
	chat: {
		height: 600,
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: theme.colors.white,
	},
	header: {
		display: 'flex',
		padding: '10px 30px',
		alignItems: 'center',
		color: theme.colors.white,
		justifyContent: 'space-between',
		backgroundColor: theme.colors.primary,
		backgroundImage: `linear-gradient(-45deg, ${Color(theme.colors.primary)
			.darken(0.4)
			.hex()} 0%, ${Color(theme.colors.primary)
			.lighten(0.2)
			.desaturate(0.2)
			.hex()} 100%)`,
	},
	aiChatOuter: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		maxHeight: 'calc(100% - 60px)',
		backgroundColor: theme.colors.white,
	},
}));

interface AiChatWindowProps {
	queryContext: Record<string, string>;
	initialConversation?: AiConversation;
	noConversationTemplate: JSX.Element;
	textareaPlaceholder: string;
	unavailableMessage?: string;
	isMicroChat?: boolean;
	onCloseButtonClick(): void;
}

export interface AiChatWindowRef {
	resetView: () => void;
	populateMessage: (message: string) => void;
}

export const AiChatWindow = ({
	queryContext,
	initialConversation,
	noConversationTemplate,
	onCloseButtonClick,
	unavailableMessage,
}: AiChatWindowProps) => {
	const classes = useStyles();
	const aiChatRef = useRef<AiChatRef>(null);

	useEffect(() => {
		if (!initialConversation) {
			return;
		}

		aiChatRef.current?.reset({
			responses: initialConversation.conversationQueries.map((conversationQuery) => ({
				id: conversationQuery.conversationQueryId,
				aiConversationId: conversationQuery.aiConversationId,
				conversationQueryId: conversationQuery.conversationQueryId,
				question: conversationQuery.query,
				markdown: conversationQuery.queryResponse ?? '',
				typeOfData: '',
				streamStatus: conversationQuery.streamStatus,
			})),
			aiResponseSourceIds: initialConversation.conversationQueries[0]?.aiResponseSourceIds ?? [],
		});
	}, [initialConversation]);

	return (
		<div className={classes.chat}>
			<div className={classes.header}>
				<div className="d-flex align-items-center">
					<Robot className="mr-2" />
					<h5 className="mb-0 font-weight-semi-bold">AskHackett&trade;</h5>
				</div>
				<Button className="p-2 text-white" variant="link" onClick={onCloseButtonClick}>
					<XLg width={20} height={20} />
				</Button>
			</div>
			<div className={classes.aiChatOuter}>
				{unavailableMessage ? (
					<p className="m-6 text-muted">{unavailableMessage}</p>
				) : (
					<AiChat
						queryContext={queryContext}
						ref={aiChatRef}
						onQuerySubmitted={() => {
							return;
						}}
						noConversationState={noConversationTemplate}
						hideSourcesDropdown
					/>
				)}
			</div>
		</div>
	);
};
